import { useState } from "react";
import { ShieldCheck, ShieldOff, QrCode, Loader2, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Section, apiError } from "./AccountSections";

function RecoveryCodesBox({ codes, onDismiss }) {
  const copy = () => {
    navigator.clipboard.writeText(codes.join("\n")).then(() => toast.success("Codes copiés"), () => toast.error("Copie impossible"));
  };
  return (
    <div className="mb-4 rounded-lg border border-amber-700/60 bg-amber-950/30 p-4" data-testid="recovery-codes-box">
      <p className="mb-2 text-sm font-semibold text-amber-400">Conservez ces {codes.length} codes de secours en lieu sûr.</p>
      <p className="mb-3 text-xs text-muted-foreground">Chaque code fonctionne une seule fois si vous perdez l'accès à votre application d'authentification.</p>
      <div className="grid grid-cols-2 gap-2 rounded-md border border-[#1E2638] bg-[#080B10] p-3 font-mono2 text-sm text-slate-200">
        {codes.map((c) => <span key={c} className="tracking-widest">{c}</span>)}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="secondary" onClick={copy} className="gap-1.5" data-testid="recovery-copy-button"><Copy className="h-3.5 w-3.5" /> Copier</Button>
        <Button size="sm" variant="ghost" onClick={onDismiss} data-testid="recovery-dismiss-button">J'ai enregistré mes codes</Button>
      </div>
    </div>
  );
}

function MfaEnabled({ user, busy, run, setCodes }) {
  const [pw, setPw] = useState("");
  const regen = () => run(async () => {
    const { data } = await api.post("/auth/mfa/recovery-codes");
    setCodes(data.recovery_codes);
    toast.success("Nouveaux codes de secours générés.");
  });
  const disable = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/auth/mfa/disable", { current_password: pw });
      toast.success("MFA désactivée. Vos autres appareils ont été déconnectés.");
      setPw("");
    }, true);
  };
  return (
    <div className="space-y-4">
      <p className="flex items-center gap-2 rounded-lg border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-400" data-testid="mfa-enabled-badge">
        <ShieldCheck className="h-4 w-4" /> La MFA est activée · {user.recovery_codes_left ?? 0} code(s) de secours restant(s).
      </p>
      <Button variant="outline" size="sm" onClick={regen} disabled={busy} className="gap-1.5" data-testid="mfa-regen-button">
        <RefreshCw className="h-4 w-4" /> Régénérer les codes de secours
      </Button>
      <form onSubmit={disable} className="space-y-3 border-t border-[#1E2638] pt-4">
        <Label className="flex items-center gap-1.5 text-muted-foreground"><ShieldOff className="h-4 w-4" /> Désactiver la MFA</Label>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Mot de passe actuel" required data-testid="mfa-disable-password" />
        <Button type="submit" variant="destructive" disabled={busy} data-testid="mfa-disable-button">Désactiver</Button>
      </form>
    </div>
  );
}

function MfaSetup({ busy, run, setCodes }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const start = () => run(async () => setSetup((await api.post("/auth/mfa/setup")).data));
  const enable = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/mfa/enable", { code });
      toast.success("MFA activée !");
      setSetup(null);
      setCode("");
      setCodes(data.recovery_codes);
    }, true, "Code invalide");
  };

  if (!setup) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Protégez votre compte avec Google Authenticator, Authy ou Microsoft Authenticator.</p>
        <Button onClick={start} disabled={busy} data-testid="mfa-setup-button" className="gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} Configurer la MFA
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">1. Scannez ce QR code avec votre application d'authentification.</p>
      <div className="flex justify-center rounded-lg border border-[#1E2638] bg-white p-3">
        <img src={setup.qr_data_uri} alt="QR code MFA" className="h-48 w-48" data-testid="mfa-qr-image" />
      </div>
      <div className="rounded-md border border-[#1E2638] bg-[#080B10] p-2 text-center">
        <span className="font-mono2 text-xs text-muted-foreground">Clé manuelle : </span>
        <span className="break-all font-mono2 text-xs text-slate-200">{setup.secret}</span>
      </div>
      <form onSubmit={enable} className="space-y-3">
        <Label>2. Saisissez le code à 6 chiffres généré</Label>
        <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456" data-testid="mfa-verify-code-input" className="font-mono2 tracking-[0.3em]" />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-enable-button">Activer la MFA</Button>
          <Button type="button" variant="ghost" onClick={() => setSetup(null)} data-testid="mfa-setup-cancel">Annuler</Button>
        </div>
      </form>
    </div>
  );
}

export function MfaSection({ user, refresh }) {
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, refreshAfter = false, fallback = "Échec") => {
    setBusy(true);
    try {
      await fn();
      if (refreshAfter) await refresh();
    } catch (err) {
      toast.error(apiError(err, fallback));
    }
    setBusy(false);
  };
  return (
    <Section icon={user.mfa_enabled ? ShieldCheck : QrCode} title="Authentification à deux facteurs (MFA)">
      {codes && <RecoveryCodesBox codes={codes} onDismiss={() => setCodes(null)} />}
      {user.mfa_enabled
        ? <MfaEnabled user={user} busy={busy} run={run} setCodes={setCodes} />
        : <MfaSetup busy={busy} run={run} setCodes={setCodes} />}
    </Section>
  );
}
